import 'components/sidebar/custom-libraries/index.css'

import { Tooltip } from '@radix-ui/themes'
import { LuBookmark } from '@react-icons/all-files/lu/LuBookmark'
import { LuCompass } from '@react-icons/all-files/lu/LuCompass'
// TODO: merge 'custom-libraries/index.css' into new sidebar css
import { LuFolder } from '@react-icons/all-files/lu/LuFolder'
import { LuFolderInput } from '@react-icons/all-files/lu/LuFolderInput'
import { LuFolderOutput } from '@react-icons/all-files/lu/LuFolderOutput'
import { LuGlobe } from '@react-icons/all-files/lu/LuGlobe'
import { LuInbox } from '@react-icons/all-files/lu/LuInbox'
import { LuStar } from '@react-icons/all-files/lu/LuStar'
import { LuTag } from '@react-icons/all-files/lu/LuTag'
import cn from 'clsx'
import memoize from 'memoize-one'
import React, { useCallback, useEffect } from 'react'

import {
  AUTO_TAGGING,
  BRIEF_INBOX,
  CUSTOM_CHANNEL,
  CUSTOM_INBOX,
  INBOX_TYPES,
  PERSONAL_LIBRARY,
  SAVED_TO_DISCO,
  TEAM_LIBRARY,
  TERRITORY,
} from '@disco/constants/GroupTypes'
import { expandSearchFolder } from '@disco/utils/groups'

import type { IndentedGroup } from 'types/group'

import HighlightedText from 'components/ui/highlighted-text'
import TextOverflow from 'components/ui/text-overflow'

import ExpandToggle from './expand'

const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation()
const getIndentStyle = memoize((indent: number) => {
  let marginLeft = 12

  if (indent) {
    marginLeft += indent * 12
  }

  return {
    marginLeft,
  }
})
type Props = {
  badgeCounter?: number
  channel?: IndentedGroup
  channels?: IndentedGroup[]
  expandable?: boolean
  expanded?: boolean
  indent?: number
  isOver?: boolean
  isReordering?: boolean
  menu?: React.ReactNode
  name: string
  channelSearchTerm?: string
  onClick?: (...args: Array<any>) => any
  onExpandClick?: (...args: Array<any>) => any
  onExpandFilter?: (...args: Array<any>) => any
  selected?: boolean
  showIcon?: boolean
  compact?: boolean
  type: string
  disabled?: boolean
}
export default function SidebarItem({
  expandable,
  isOver,
  expanded,
  onExpandClick,
  onExpandFilter,
  channelSearchTerm,
  channel,
  channels,
  ...props
}: Props): React.ReactElement<'div'> {
  let icon
  const badgeCounter = props.badgeCounter
  const groupType = props.type
  const selected = props.selected || null
  const menu = props.menu
  if (props.showIcon !== false) {
    if (INBOX_TYPES.includes(groupType)) {
      icon = <LuInbox size={16} />
    } else if (groupType === CUSTOM_CHANNEL) {
      if (channel?.business_role === 'partner') {
        icon = <LuFolderInput size={16} />
      } else if (
        channel?.shared_with_businesses?.some(b =>
          ['accepted', 'pending'].includes(b.status),
        )
      ) {
        icon = <LuFolderOutput size={16} />
      } else {
        icon = <LuFolder size={16} />
      }
    } else if (groupType === TEAM_LIBRARY) {
      icon = <LuCompass size={16} />
    } else if (groupType === TERRITORY) {
      icon = <LuGlobe size={16} />
    } else if (groupType === PERSONAL_LIBRARY) {
      icon = <LuStar size={16} />
    } else if (groupType === AUTO_TAGGING) {
      icon = <LuTag size={16} />
    } else if (groupType === SAVED_TO_DISCO) {
      icon = <LuBookmark size={16} />
    }
  }

  const handleExpandFilteredFolders = useCallback(() => {
    let expandedFolders

    if (channels && channelSearchTerm) {
      expandedFolders = expandSearchFolder(channels, channelSearchTerm)
    }

    if (onExpandFilter)
      onExpandFilter(channelSearchTerm, channel, expandedFolders)
  }, [channel, channelSearchTerm, channels, onExpandFilter])
  useEffect(() => {
    handleExpandFilteredFolders()
  }, [handleExpandFilteredFolders, channelSearchTerm])
  const itemEl = (
    <div
      className={cn('Sidebar_BaseItem Sidebar_Item', {
        'Sidebar_Item--is-over': isOver,
        'Sidebar_Item--is-reordering': props.isReordering,
        'Sidebar_Item--disabled': props.disabled,
      })}
      data-sel={selected}
      onClick={props.onClick}
      style={getIndentStyle(props.indent ?? 0)}
    >
      {expandable && (
        <div className="Sidebar_LeftGutter">
          <ExpandToggle onClick={onExpandClick} expanded={expanded} />
        </div>
      )}
      <div className="Sidebar_ItemIcon">{icon}</div>
      <div
        className="Sidebar_Label"
        translate={
          groupType === CUSTOM_CHANNEL ||
          groupType === CUSTOM_INBOX ||
          groupType === BRIEF_INBOX
            ? 'no'
            : undefined
        }
      >
        {channelSearchTerm && groupType === CUSTOM_CHANNEL ? (
          <HighlightedText
            customColors={{
              background: 'transparent',
              text: '#00ba34',
            }}
            text={props.name}
            highlights={[channelSearchTerm]}
          />
        ) : (
          <TextOverflow content={props.name} />
        )}
      </div>
      {badgeCounter && badgeCounter > 0 ? (
        <div
          className={cn('Sidebar_NotificationCont', {
            'Sidebar_NotificationCont--compact': props.compact,
          })}
        >
          <div className="Sidebar_Notification"> {badgeCounter} </div>
        </div>
      ) : null}
      {menu && (
        <div
          className="Sidebar_ActionButton"
          onClick={stopPropagation}
          role="presentation"
        >
          {menu}
        </div>
      )}
    </div>
  )

  return props.compact ? (
    <Tooltip className="Sidebar_NavTooltip" content={props.name} side="right">
      {itemEl}
    </Tooltip>
  ) : (
    itemEl
  )
}
